import React from "react";
import styled from "styled-components";
import useMediaQuery from "@mui/material/useMediaQuery";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors } from "../../../Shared/colors";
import { Card, CardHeader, CHART_PLOT_HEIGHT } from "./socialLeadsStyles";
import { formatDuration } from "./socialLeadsFormat";

/**
 * How quickly each person makes first contact, fastest first.
 *
 * Ordered fastest-first rather than slowest-first deliberately: the same data
 * either way, but a list that opens with whoever is doing well reads as a
 * standard to meet rather than as a list of who to reprimand.
 *
 * The figure is a median, matching the "Typical time to first contact" strip
 * above — an average lets one lead forgotten over a weekend put an otherwise
 * prompt person at the bottom, and a week only gives each person a handful of
 * leads for it to distort.
 *
 * Oversight roles only. Whether the rotation is fair and who is slow to call
 * are questions for whoever acts on them, not for the people being ranked.
 */

/**
 * Height of one employee's row.
 *
 * The plot is sized from this rather than the other way round, so a large team
 * scrolls at a readable row height instead of being compressed into bars a few
 * pixels apart.
 */
const ROW_HEIGHT = 30;

/**
 * A name too long for the axis gutter, shortened.
 *
 * The gutter is a fixed width, so an untruncated long name is drawn straight
 * through the bars beside it. The tooltip carries the full name.
 */
const truncateName = (name, limit) =>
  name.length > limit ? `${name.slice(0, limit - 1).trimEnd()}…` : name;

const ResponseTooltip = ({ active, payload, slaMinutes }) => {
  if (!active || !payload?.length) return null;

  const entry = payload[0].payload;
  const late = entry.contacted - entry.withinSla;

  return (
    <TooltipBox>
      <p className="head">{entry.name}</p>
      <p className="line">
        Typical<strong>{formatDuration(entry.medianMinutes)}</strong>
      </p>
      <p className="line">
        Within {formatDuration(slaMinutes)}
        <strong>
          {entry.withinSla} of {entry.contacted}
        </strong>
      </p>
      {late > 0 ? (
        <p className="line">
          Slowest<strong>{formatDuration(entry.slowestMinutes)}</strong>
        </p>
      ) : null}
      {entry.awaiting > 0 ? (
        <p className="line warn">
          Not yet contacted<strong>{entry.awaiting}</strong>
        </p>
      ) : null}
    </TooltipBox>
  );
};

export const SocialLeadsResponseChart = ({ employees, slaMinutes }) => {
  // The axis gutter and the room for the value labels are a fixed number of
  // pixels, so on a narrow card they would take most of the width and leave
  // the bars themselves too short to compare.
  const isNarrow = useMediaQuery("(max-width: 620px)");
  const axisWidth = isNarrow ? 86 : 128;
  const labelGutter = isNarrow ? 48 : 62;
  const nameLimit = isNarrow ? 11 : 17;

  if (!employees) return null;

  if (employees.length === 0) {
    return (
      <ChartCard>
        <CardHeader>
          <TimerOutlinedIcon />
          <h2>Response time by employee</h2>
        </CardHeader>
        <Placeholder>
          No leads in this range have been contacted yet, so there is no
          response time to report.
        </Placeholder>
      </ChartCard>
    );
  }

  const slowest = Math.max(...employees.map((entry) => entry.medianMinutes));
  // Leave room for the SLA line even when everyone beat it, otherwise the
  // reference line sits on the axis edge and reads as part of the frame.
  const domainMax = Math.max(slowest, slaMinutes || 0) * 1.15;

  return (
    <ChartCard>
      <CardHeader>
        <TimerOutlinedIcon />
        <h2>Response time by employee</h2>
        <span className="unit">Fastest first</span>
      </CardHeader>

      <Body>
        {/* What the bars measure, and what the dashed line and the two colours
            mean. This was previously only a label on the reference line
            itself, where the chart's top margin clipped it. */}
        <Caption>
          Typical (median) time to first contact
        </Caption>
        {/* Without a threshold there is no line to explain and every bar is the
            same colour, so the legend would be describing distinctions the
            chart is not drawing. */}
        {slaMinutes ? (
          <Legend>
            <span className="key">
              <i className="swatch ok" />
              Within {formatDuration(slaMinutes)}
            </span>
            <span className="key">
              <i className="swatch late" />
              Over {formatDuration(slaMinutes)}
            </span>
            <span className="key">
              <i className="dash" />
              {formatDuration(slaMinutes)} target
            </span>
          </Legend>
        ) : null}

        <Plot>
          <ResponsiveContainer
            width="100%"
            height={Math.max(employees.length * ROW_HEIGHT + 30, CHART_PLOT_HEIGHT)}
          >
            <BarChart
              data={employees}
              layout="vertical"
              margin={{ top: 6, right: labelGutter, bottom: 6, left: 4 }}
              barCategoryGap="26%"
            >
              <XAxis type="number" hide domain={[0, domainMax]} />
              <YAxis
                type="category"
                dataKey="name"
                width={axisWidth}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: isNarrow ? 10.5 : 11.5, fill: colors.text }}
                tickFormatter={(name) => truncateName(name, nameLimit)}
              />
              <Tooltip
                cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                content={<ResponseTooltip slaMinutes={slaMinutes} />}
              />
              {/* No label on the line itself — at this chart's top margin it was
                  clipped, and near the right of the domain it collided with the
                  value labels. The legend above names it instead, where nothing
                  can crop it. */}
              {slaMinutes ? (
                <ReferenceLine
                  x={slaMinutes}
                  stroke={colors.textLight}
                  strokeDasharray="4 4"
                />
              ) : null}
              <Bar dataKey="medianMinutes" radius={[0, 4, 4, 0]} maxBarSize={18}>
                {employees.map((entry) => (
                  // Past the target turns red — but the duration is printed on
                  // every bar and the target line is drawn, so the colour is a
                  // second reading of something already legible without it.
                  <Cell
                    key={entry.employeeId}
                    fill={
                      slaMinutes && entry.medianMinutes > slaMinutes
                        ? colors.veryHot
                        : colors.brown
                    }
                  />
                ))}
                <LabelList
                  dataKey="medianMinutes"
                  position="right"
                  formatter={formatDuration}
                  style={{ fontSize: 11, fill: colors.textMed }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Plot>
      </Body>
    </ChartCard>
  );
};

/* Fills the grid row so this card ends level with the volume chart beside it,
   rather than growing a row taller with every employee. */
const ChartCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  padding: 12px 16px 10px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/* Capped at the shared plot height and scrolled past it. Each bar keeps a
   readable row rather than the whole team being squeezed into a fixed box, and
   the caption and legend above stay put while the bars move. */
const Plot = styled.div`
  flex: 1;
  min-height: 0;
  max-height: ${CHART_PLOT_HEIGHT}px;
  overflow-y: auto;
`;

const Caption = styled.p`
  margin: 0 0 7px 0;
  font-size: 0.74rem;
  color: ${colors.textMed};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.85rem;
  margin-bottom: 10px;

  .key {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    color: ${colors.textMed};
    white-space: nowrap;
  }

  .swatch {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
    flex-shrink: 0;
  }
  .swatch.ok {
    background: ${colors.brown};
  }
  .swatch.late {
    background: ${colors.veryHot};
  }

  /* Mirrors the dashed reference line drawn in the chart. */
  .dash {
    width: 15px;
    height: 0;
    flex-shrink: 0;
    border-top: 2px dashed ${colors.textLight};
  }
`;

const Placeholder = styled.p`
  margin: 0;
  padding: 26px 18px 30px 18px;
  text-align: center;
  font-size: 0.8rem;
  color: ${colors.textMed};
`;

const TooltipBox = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 9px;
  padding: 9px 11px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);

  .head {
    margin: 0 0 6px 0;
    font-size: 0.74rem;
    font-weight: 700;
    color: ${colors.text};
  }

  .line {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 3px 0;
    font-size: 0.74rem;
    color: ${colors.textMed};
  }

  .line:last-child {
    margin-bottom: 0;
  }

  .line strong {
    margin-left: auto;
    padding-left: 16px;
    font-variant-numeric: tabular-nums;
    color: ${colors.text};
  }

  .line.warn,
  .line.warn strong {
    color: ${colors.moderate};
  }
`;
